import React, { JSX } from 'react';
import {
  Field,
  Text,
  Image,
  ImageField,
  RichText,
  LinkField,
  Link,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Icons from '../atoms/Icons';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';
interface PromptedActionsParentProps extends StyleguideComponentProps {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Headline: Field<string>;
    'Headline H1': Field<boolean>;
    Actions: ActionChild[];
  };
}

interface ActionChild {
  id: string;
  name: string;
  displayName: string;
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    ActionText?: Field<string>;
    Image?: ImageField;
    ActionLink?: LinkField;
    BasePhoneNumber?: Field<string>;
  };
}

const PromptedActionsParent = (
  props: PromptedActionsParentProps
): JSX.Element => {
  const { fields } = props;
  const marginSize = getMarginSize(fields.MarginSize?.fields?.heading?.value);
  const { Headline, 'Headline H1': HeadlineH1, Actions } = fields;

  const HeadingTag = HeadlineH1?.value ? 'h1' : 'h2';
  const headingClass = HeadlineH1?.value ? 'osf-display-h1' : 'osf-headline-h2';

  const handleTouchStart = (e: React.TouchEvent<HTMLAnchorElement>) => {
    const el = e.currentTarget;
    el.classList.add('is-pressed');
    setTimeout(() => {
      el.classList.remove('is-pressed');
    }, 300);
  };

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`prompted-actions-parent-${props.rendering?.uid}`}
    >
      <div className="jss-inner-container">
        <div className="prompted-actions-parent">
          <div className="prompted-Headline">
            {Headline?.value && (
              <Text
                field={Headline}
                tag={HeadingTag}
                className={`headline ${headingClass}`}
              />
            )}
          </div>
          <div className="actions-list" role="list">
            {Actions.map(({ id, fields }) => {
              const {
                ActionText,
                Image: ImageField,
                ActionLink,
                MarginSize,
                BasePhoneNumber,
              } = fields || {};
              const phoneNumber = BasePhoneNumber?.value?.trim();
              const hasActionText = Boolean(ActionText?.value?.trim());
              const CardContent = (
                <>
                  <div className="action-card-icon" aria-hidden="true">
                    {ImageField?.value?.src && (
                      <Image field={ImageField} className="icon-image" />
                    )}
                  </div>
                  <div className="action-card-text osf-display-h3">
                    <span className="action-text-with-icon">
                      {hasActionText ? (
                        <RichText
                          field={ActionText}
                          className="action-richtext"
                        />
                      ) : (
                        phoneNumber && (
                          <span className="action-richtext">
                            {phoneNumber}
                          </span>
                        )
                      )}
                      <Icons id="chevron-next" className="chevron-icon" />
                    </span>
                  </div>

                </>
              );

              return phoneNumber ? (
                <a
                  key={id}
                  href={`tel:${phoneNumber}`}
                  className={classNames(
                    'link action-card',
                    getMarginSize(MarginSize?.fields?.heading?.value)
                  )}
                  role="listitem"
                  onTouchStart={handleTouchStart}
                >
                  {CardContent}
                </a>
              ) : ActionLink?.value?.href && ActionLink ? (
                <Link
                  key={id}
                  className={classNames(
                    'link action-card',
                    getMarginSize(MarginSize?.fields?.heading?.value)
                  )}
                  field={ActionLink}
                  role="listitem"
                  onTouchStart={handleTouchStart}
                >
                  {CardContent}
                </Link>
              ) : (
                <div
                  key={id}
                  className={classNames(
                    'action-card',
                    getMarginSize(MarginSize?.fields?.heading?.value)
                  )}
                  role="listitem"
                  tabIndex={0}
                >
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptedActionsParent;
